import { FaultCodes } from '@feedflo/fault-codes';

/** Fault codes whose duration alert may be set to 0 hours (stored as 1 second). */
export const ALERTS_ALLOWING_ZERO_HOURS = [FaultCodes.BIN_CONTROLLER_HARD_LOCK_DOWN];

export const ALERTS_WITH_NO_FEED_TIME = [FaultCodes.EMPTY_PIPE, FaultCodes.INACTIVE_AUGER];

export const IMMEDIATE_ONE_SHOT_ALERTS = [FaultCodes.BIN_CONTROLLER_SLIDE_OPEN];

export const IMMEDIATE_THRESHOLD_SECONDS = 1;

/** Short timing labels shown beside hour inputs in tier settings editor. */
export const EDITOR_DURATION_TIMING_LABELS = {
  [FaultCodes.EMPTY_PIPE]: 'after empty pipe is detected',
  [FaultCodes.AUGER_RUNNING]: 'after auger runs continuously',
  [FaultCodes.BIN_CONTROLLER_HARD_LOCK_DOWN]: 'after lockdown begins',
  [FaultCodes.DEVICE_UNREACHABLE]: 'after device goes offline',
};

export const DEFAULT_EDITOR_DURATION_TIMING_LABEL = 'after alert begins';

export const EDITOR_NO_FEED_TIMING_LABELS = {
  before_empty: 'before feeders are empty',
  after_empty: 'after feeders are empty',
};

export function getEditorDurationTimingLabel(faultCode) {
  return EDITOR_DURATION_TIMING_LABELS[Number(faultCode)] ?? DEFAULT_EDITOR_DURATION_TIMING_LABEL;
}

export function allowsZeroAlertHours(faultCode) {
  return ALERTS_ALLOWING_ZERO_HOURS.includes(Number(faultCode));
}

export function isImmediateOneShotAlert(faultCode) {
  return IMMEDIATE_ONE_SHOT_ALERTS.includes(Number(faultCode));
}

export function alertThresholdSecondsToHours(seconds, faultCode) {
  if (allowsZeroAlertHours(faultCode) && Number(seconds) === IMMEDIATE_THRESHOLD_SECONDS) {
    return 0;
  }
  return Number(seconds) / 3600;
}

export function alertHoursToThresholdSeconds(hours, faultCode) {
  if (hours === null || hours === undefined || hours === '') {
    return allowsZeroAlertHours(faultCode) ? IMMEDIATE_THRESHOLD_SECONDS : 0;
  }

  const numericHours = Number(hours);
  if (allowsZeroAlertHours(faultCode) && numericHours === 0) {
    return IMMEDIATE_THRESHOLD_SECONDS;
  }

  return Math.round(numericHours * 3600);
}

export function isImmediateAlertThresholdSeconds(seconds) {
  return Math.abs(Number(seconds)) <= IMMEDIATE_THRESHOLD_SECONDS;
}

export function isImmediateAlertThresholdHours(hours) {
  return Math.round(Math.abs(Number(hours)) * 100) / 100 === 0;
}

export function formatHoursDisplay(hours) {
  const rounded = Math.round(Math.abs(Number(hours)) * 100) / 100;
  if (rounded === 0) {
    return 'Immediately';
  }
  return `${rounded} hour${rounded === 1 ? '' : 's'}`;
}

export function formatReminderSummary(hours) {
  const rounded = Math.round(Math.abs(Number(hours)) * 100) / 100;
  if (!rounded) {
    return '';
  }
  return `. Reminders every ${formatHoursDisplay(hours).toLowerCase()} until resolved`;
}

export function formatResolvedSummary(faultCode) {
  switch (Number(faultCode)) {
    case FaultCodes.EMPTY_PIPE:
      return '. When the pipe is no longer empty.';
    case FaultCodes.INACTIVE_AUGER:
      return '. When the auger is active again.';
    case FaultCodes.AUGER_RUNNING:
      return '. When the auger stops running continuously.';
    case FaultCodes.DEVICE_UNREACHABLE:
      return '. When the device is back online.';
    case FaultCodes.BIN_CONTROLLER_HARD_LOCK_DOWN:
      return '. When lockdown ends.';
    default:
      return '. When the issue is resolved.';
  }
}

function formatImmediateDurationSummary(faultCode) {
  switch (Number(faultCode)) {
    case FaultCodes.BIN_CONTROLLER_SLIDE_OPEN:
      return 'Immediately when slide opens';
    case FaultCodes.BIN_CONTROLLER_HARD_LOCK_DOWN:
      return 'Immediately when lockdown begins';
    case FaultCodes.DEVICE_UNREACHABLE:
      return 'Immediately when device goes offline';
    case FaultCodes.AUGER_RUNNING:
      return 'Immediately when auger starts running continuously';
    default:
      return null;
  }
}

function formatDurationAlertSummary(hours, faultCode, isImmediate) {
  if (isImmediate) {
    const immediateText = formatImmediateDurationSummary(faultCode);
    if (immediateText) {
      return immediateText;
    }
  }

  const prefix = isImmediate ? 'Immediately' : formatHoursDisplay(hours);

  switch (Number(faultCode)) {
    case FaultCodes.EMPTY_PIPE:
      return `${prefix} after empty pipe is detected`;
    case FaultCodes.AUGER_RUNNING:
      return `${prefix} after auger runs continuously`;
    case FaultCodes.DEVICE_UNREACHABLE:
      return `${prefix} after device goes offline`;
    case FaultCodes.BIN_CONTROLLER_HARD_LOCK_DOWN:
      return `${prefix} after lockdown begins`;
    default:
      return `${prefix} after alert begins`;
  }
}

function formatNoFeedAlertSummary(hours, thresholdSeconds) {
  const prefix = formatHoursDisplay(hours);
  const isBefore =
    thresholdSeconds != null ? Number(thresholdSeconds) < 0 : Number(hours) < 0;

  return isBefore
    ? `${prefix} before feeders are empty`
    : `${prefix} after feeders are empty`;
}

/**
 * Human-readable alert timing for tier notification summaries.
 * Accepts either GraphQL threshold fields or in-editor hour values.
 */
export function formatAlertThresholdSummary({ faultCode, thresholdSeconds, thresholdType, hours }) {
  const code = Number(faultCode);

  const resolvedHours =
    hours != null ? Number(hours) : alertThresholdSecondsToHours(thresholdSeconds, code);

  const isImmediate =
    thresholdSeconds != null
      ? isImmediateAlertThresholdSeconds(thresholdSeconds)
      : isImmediateAlertThresholdHours(resolvedHours);

  if (isImmediateOneShotAlert(code) && isImmediate) {
    return formatDurationAlertSummary(resolvedHours, code, true);
  }

  if (ALERTS_WITH_NO_FEED_TIME.includes(code) && thresholdType === 'nofeed_time') {
    return formatNoFeedAlertSummary(resolvedHours, thresholdSeconds);
  }

  return formatDurationAlertSummary(resolvedHours, code, isImmediate);
}
